import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { confirmDelete } from '../utils/confirmDelete';
import ModalPortal from '../components/ModalPortal';
import {
  Building2,
  Plus,
  Settings,
  Users,
  Crown,
  Shield,
  User,
  Trash2,
  Edit,
  LogOut,
  Brain,
  Calendar,
  Search,
  ChevronUp,
  ChevronDown,
  CreditCard
} from 'lucide-react';

const WorkspaceManagement = () => {
  const { user, refreshUser } = useAuth();
  const {
    currentWorkspace,
    workspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    switchWorkspace,
    removeMember,
    updateMemberRole,
    leaveWorkspace,
    getWorkspaceMembers
  } = useWorkspace();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState(null);

  const [newWorkspace, setNewWorkspace] = useState({
    name: '',
    description: ''
  });

  const [editWorkspace, setEditWorkspace] = useState({
    name: '',
    description: ''
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [activeTab, setActiveTab] = useState('workspaces');
  const [adminUsersPlans, setAdminUsersPlans] = useState({ users: [], plans: [] });
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [userPlanSavingId, setUserPlanSavingId] = useState(null);
  const [userDeletingId, setUserDeletingId] = useState(null);

  const isAccountAdmin = String(user?.role || '').toLowerCase() === 'admin';

  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);

  useEffect(() => {
    if (activeTab === 'userPlans') {
      setShowMembers(false);
    }
  }, [activeTab]);

  const loadUsersPlans = useCallback(async () => {
    if (!isAccountAdmin) return;
    setAdminUsersLoading(true);
    try {
      const res = await apiClient.get('/api/admin/users-plans');
      setAdminUsersPlans({
        users: res.data?.users || [],
        plans: res.data?.plans || []
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load users');
      setAdminUsersPlans({ users: [], plans: [] });
    } finally {
      setAdminUsersLoading(false);
    }
  }, [isAccountAdmin]);

  useEffect(() => {
    if (isAccountAdmin && activeTab === 'userPlans') {
      loadUsersPlans();
    }
  }, [isAccountAdmin, activeTab, loadUsersPlans]);

  const handleUserPlanChange = useCallback(
    async (targetUserId, planIdValue) => {
      const plan_id = Number(planIdValue);
      if (!targetUserId || !plan_id) return;
      setUserPlanSavingId(targetUserId);
      try {
        await apiClient.put(`/api/admin/users/${targetUserId}/plan`, { plan_id });
        toast.success('User plan updated');
        await loadUsersPlans();
      } catch (e) {
        toast.error(e.response?.data?.error || 'Failed to update plan');
      } finally {
        setUserPlanSavingId(null);
      }
    },
    [loadUsersPlans]
  );

  const handleAdminDeleteUser = useCallback(
    async (targetUser) => {
      if (!targetUser?.id || targetUser.id === user?.id) return;
      const label = targetUser.name || targetUser.email || 'this user';
      if (
        !confirmDelete(
          `Permanently delete account ${label}?\n\nThis cannot be undone. If they own workspaces, deletion will be blocked until those workspaces are removed or reassigned.`
        )
      ) {
        return;
      }
      setUserDeletingId(targetUser.id);
      try {
        await apiClient.delete(`/api/admin/users/${targetUser.id}`);
        toast.success('User deleted');
        await loadUsersPlans();
      } catch (e) {
        toast.error(e.response?.data?.error || 'Failed to delete user');
      } finally {
        setUserDeletingId(null);
      }
    },
    [loadUsersPlans, user?.id]
  );

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!newWorkspace.name.trim()) return;

    setLoading(true);
    const result = await createWorkspace(newWorkspace.name, newWorkspace.description);
    if (result.success) {
      setNewWorkspace({ name: '', description: '' });
      setShowCreateForm(false);
    }
    setLoading(false);
  };

  const handleEditWorkspace = async (e) => {
    e.preventDefault();
    if (!editWorkspace.name.trim() || !editingWorkspace) return;

    setLoading(true);
    const result = await updateWorkspace(editingWorkspace.id, editWorkspace);
    if (result.success) {
      setEditWorkspace({ name: '', description: '' });
      setEditingWorkspace(null);
      setShowEditForm(false);
    }
    setLoading(false);
  };

  const startEditWorkspace = (workspace) => {
    setEditingWorkspace(workspace);
    setEditWorkspace({
      name: workspace.name,
      description: workspace.description || ''
    });
    setShowEditForm(true);
  };

  const cancelEdit = () => {
    setEditingWorkspace(null);
    setEditWorkspace({ name: '', description: '' });
    setShowEditForm(false);
  };

  const loadMembers = async () => {
    setLoading(true);
    const result = await getWorkspaceMembers(currentWorkspace.id);
    if (result.success) {
      setMembers(result.members);
      setShowMembers(true);
    }
    setLoading(false);
  };

  const handleRemoveMember = async (userId) => {
    if (confirmDelete('Are you sure you want to remove this member?')) {
      setLoading(true);
      await removeMember(currentWorkspace.id, userId);
      await loadMembers(); // Reload members
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    setLoading(true);
    await updateMemberRole(currentWorkspace.id, userId, newRole);
    await loadMembers(); // Reload members
    setLoading(false);
  };

  const handleLeaveWorkspace = async (workspaceId) => {
    if (confirmDelete('Are you sure you want to leave this workspace?')) {
      setLoading(true);
      await leaveWorkspace(workspaceId);
      setLoading(false);
    }
  };

  const handleDeleteWorkspace = async (workspaceId) => {
    if (confirmDelete('Are you sure you want to delete this workspace? This action cannot be undone.')) {
      setLoading(true);
      await deleteWorkspace(workspaceId);
      setLoading(false);
    }
  };

  const filteredAndSortedWorkspaces = useMemo(() => {
    let list = workspaces;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (w) =>
          (w.name || '').toLowerCase().includes(q) ||
          (w.description || '').toLowerCase().includes(q) ||
          (w.creator_name || '').toLowerCase().includes(q) ||
          (w.creator_email || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'description':
          aVal = (a.description || '').toLowerCase();
          bVal = (b.description || '').toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'total_users':
          aVal = a.total_users ?? a.member_count ?? 0;
          bVal = b.total_users ?? b.member_count ?? 0;
          break;
        case 'user_role':
          aVal = (a.user_role || '').toLowerCase();
          bVal = (b.user_role || '').toLowerCase();
          break;
        default:
          aVal = a.name || '';
          bVal = b.name || '';
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [workspaces, searchQuery, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ChevronUp className="w-4 h-4 opacity-30" />;
    return sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'admin':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No workspace selected</p>
      </div>
    );
  }

  const showWorkspacesTab = !isAccountAdmin || activeTab === 'workspaces';
  const showUsersPlansTab = isAccountAdmin && activeTab === 'userPlans';

  return (
    <div className="space-y-6">
      {/* Title + account-admin tabs */}
      <div className="space-y-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workspace Management</h1>
            <p className="text-gray-600">
              {showUsersPlansTab
                ? 'Login accounts with role User or Admin — assign plans or remove an account (workspace-only members are not listed here).'
                : 'Manage your workspaces and team members.'}
            </p>
          </div>
        </div>

        {isAccountAdmin && (
          <div className="flex rounded-xl border border-gray-200 bg-gray-50/80 p-1.5 w-full max-w-md">
            <button
              type="button"
              onClick={() => setActiveTab('workspaces')}
              className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                activeTab === 'workspaces'
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Building2 className="w-4 h-4 shrink-0" />
              Workspaces
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('userPlans')}
              className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                activeTab === 'userPlans'
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CreditCard className="w-4 h-4 shrink-0" />
              Users Plans
            </button>
          </div>
        )}
      </div>

      {showWorkspacesTab && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={loadMembers}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <Users className="w-4 h-4" />
            <span>View Members</span>
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            <span>Create Workspace</span>
          </button>
        </div>
      )}

      {/* Users Plans (admin) */}
      {showUsersPlansTab && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Users &amp; subscription plans
            </h2>
            <button
              type="button"
              onClick={loadUsersPlans}
              disabled={adminUsersLoading}
              className="text-sm px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {adminUsersLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {adminUsersLoading && adminUsersPlans.users.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">Loading users…</div>
          ) : (
            <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[220px]">
                      Assign plan
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase w-[100px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {adminUsersPlans.users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{u.name || '—'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{u.email}</td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            u.account_role === 'admin'
                              ? 'bg-violet-100 text-violet-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {u.account_role === 'admin' ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700">
                        <span>{u.plan_name || '—'}</span>
                        {u.subscription_status && (
                          <span className="ml-2 text-xs text-gray-500">({u.subscription_status})</span>
                        )}
                        {u.uses_implicit_free && (
                          <span
                            className="ml-1 text-xs text-amber-600"
                            title="No subscription row yet; defaulting to Free plan"
                          >
                            default
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <select
                          value={u.plan_id ?? ''}
                          disabled={userPlanSavingId === u.id || adminUsersPlans.plans.length === 0}
                          onChange={(e) => handleUserPlanChange(u.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 max-w-[260px] bg-white focus:ring-2 focus:ring-blue-500"
                        >
                          {adminUsersPlans.plans.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {!p.is_active ? ' (inactive)' : ''}
                              {p.is_public === false ? ' — internal' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-3 text-right">
                        {u.id === user?.id ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <button
                            type="button"
                            title="Delete user account"
                            disabled={userDeletingId === u.id || userPlanSavingId === u.id}
                            onClick={() => handleAdminDeleteUser(u)}
                            className="inline-flex items-center justify-center p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {userDeletingId === u.id ? (
                              <span className="text-xs text-gray-500">…</span>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Workspaces Table */}
      {showWorkspacesTab && (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-blue-600" />
            Select Workspace
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search workspaces..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-64"
            />
          </div>
        </div>

        {workspaces.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No workspaces found</p>
            <p className="text-sm text-gray-400">Create your first workspace to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[calc(100vh-320px)] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Workspace
                      <SortIcon column="name" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('description')}
                  >
                    <div className="flex items-center gap-1">
                      Description
                      <SortIcon column="description" />
                    </div>
                  </th>
                  {currentWorkspace?.id === 1 && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-1">
                          Created Date
                          <SortIcon column="created_at" />
                        </div>
                      </th>
                    </>
                  )}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('total_users')}
                  >
                    <div className="flex items-center gap-1">
                      Total Users
                      <SortIcon column="total_users" />
                    </div>
                  </th>
                  {currentWorkspace?.id === 1 && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      AI Usage
                    </th>
                  )}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('user_role')}
                  >
                    <div className="flex items-center gap-1">
                      Your Role
                      <SortIcon column="user_role" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedWorkspaces.length === 0 ? (
                  <tr>
                    <td colSpan={currentWorkspace?.id === 1 ? 8 : 5} className="px-6 py-12 text-center text-gray-500">
                      No workspaces match your search.
                    </td>
                  </tr>
                ) : filteredAndSortedWorkspaces.map((workspace) => (
                  <tr key={workspace.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{workspace.name}</div>
                          <div className="text-sm text-gray-500">ID: {workspace.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {workspace.description || 'No description'}
                      </div>
                    </td>
                    {currentWorkspace?.id === 1 && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-1" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{workspace.creator_name || 'Unknown'}</div>
                              <div className="text-xs text-gray-500">{workspace.creator_email || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm text-gray-900">
                              {workspace.created_at ? new Date(workspace.created_at).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">{workspace.total_users || workspace.member_count || 0}</span>
                      </div>
                    </td>
                    {currentWorkspace?.id === 1 && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="flex items-center mb-1">
                            <Brain className="w-4 h-4 text-purple-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900">{workspace.ai_uses || 0} uses</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {workspace.ai_tokens ? `${parseInt(workspace.ai_tokens).toLocaleString()} tokens` : '0 tokens'}
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {workspace.user_role ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(workspace.user_role)}`}>
                          {getRoleIcon(workspace.user_role)}
                          <span className="ml-1 capitalize">{workspace.user_role}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not a member</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {workspace.user_role && (
                          <button
                            onClick={() => switchWorkspace(workspace)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Switch to this workspace"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        )}
                        {workspace.user_role && ['owner', 'admin'].includes(workspace.user_role) && (
                          <button
                            onClick={() => startEditWorkspace(workspace)}
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title="Edit workspace"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {(workspace.id !== 1 && (workspace.user_role === 'owner' || isAccountAdmin)) ? (
                          <button
                            onClick={() => handleDeleteWorkspace(workspace.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete workspace"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : null}
                        {workspace.id !== 1 && workspace.user_role && workspace.user_role !== 'owner' && (
                          <button
                            onClick={() => handleLeaveWorkspace(workspace.id)}
                            className="text-orange-600 hover:text-orange-900 p-1"
                            title="Leave workspace"
                          >
                            <LogOut className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
                }
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Members List */}
      {showMembers && showWorkspacesTab && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-600" />
            Workspace Members
          </h3>

          <div className="space-y-3">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{member.name}</p>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleColor(member.role)}`}>
                    {getRoleIcon(member.role)}
                    <span className="ml-1 capitalize">{member.role}</span>
                  </span>

                  {currentWorkspace.user_role === 'owner' && member.role !== 'owner' && (
                    <div className="flex items-center space-x-2">
                      <select
                        value={['member', 'agent', 'viewer'].includes(member.role) ? member.role : ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!v) return;
                          handleUpdateRole(member.user_id, v);
                        }}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        {member.role === 'admin' && <option value="">Change to…</option>}
                        <option value="member">Member</option>
                        <option value="agent">Agent</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Workspace Modal */}
      {showCreateForm && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Workspace</h3>
            <form onSubmit={handleCreateWorkspace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workspace Name
                </label>
                <input
                  type="text"
                  placeholder="Enter workspace name"
                  value={newWorkspace.name}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  placeholder="Enter workspace description"
                  value={newWorkspace.description}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Workspace'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Edit Workspace Modal */}
      {showEditForm && editingWorkspace && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Workspace</h3>
            <form onSubmit={handleEditWorkspace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workspace Name
                </label>
                <input
                  type="text"
                  placeholder="Enter workspace name"
                  value={editWorkspace.name}
                  onChange={(e) => setEditWorkspace({ ...editWorkspace, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  placeholder="Enter workspace description"
                  value={editWorkspace.description}
                  onChange={(e) => setEditWorkspace({ ...editWorkspace, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Workspace'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default WorkspaceManagement;
